<template>
  <span class="aml-badge" :class="badgeClass" :title="tooltip" @click.stop="$emit('click')">
    <span v-if="loading" class="aml-spin"></span>
    <span v-else-if="result.error && !result.risk_level" class="aml-icon">?</span>
    <template v-else>
      <span class="aml-dot" :style="{ background: riskColor }"></span>
      <span v-if="!compact" class="aml-text">{{ riskLabel }}</span>
      <span v-if="!compact && blockedSignals.length" class="aml-tags">
        <span v-for="sig in blockedSignals.slice(0, 2)" :key="sig.name" class="aml-tag">
          {{ sigMeta(sig.name).icon }} {{ sigMeta(sig.name).label }}
        </span>
        <span v-if="blockedSignals.length > 2" class="aml-tag-more">+{{ blockedSignals.length - 2 }}</span>
      </span>
    </template>
  </span>
</template>

<script>
import { checkAddress, CHAIN_AML, RISK_COLOR, RISK_LABEL, SIGNAL_META } from '../services/amlService';

export default {
  name: 'AmlBadge',
  props: {
    address:  { type: String, required: true },
    chain:    { type: String, required: true },  // 'tron' | 'ethereum' | 'bsc' | 'polygon'
    compact:  { type: Boolean, default: false },
    autoCheck:{ type: Boolean, default: true },
  },
  emits: ['result', 'blocked', 'click'],

  data() {
    return { loading: false, result: { risk_level: 'unknown', signals: [], error: null } };
  },

  computed: {
    blockchain() { return CHAIN_AML[this.chain] || 'ETH'; },
    riskColor()  { return RISK_COLOR[this.result.risk_level] || RISK_COLOR.unknown; },
    riskLabel()  { return RISK_LABEL[this.result.risk_level] || '—'; },
    badgeClass() {
      return [
        'aml-' + (this.result.risk_level || 'unknown'),
        this.result.hardBlocked ? 'aml-hardblock' : '',
        this.result.softBlocked ? 'aml-softblock' : '',
        this.compact ? 'aml-compact' : '',
      ];
    },
    blockedSignals() {
      return (this.result.signals || []).filter(s =>
        SIGNAL_META[s.name]
      );
    },
    tooltip() {
      if (this.loading) return 'Checking…';
      if (this.result.error) return 'AML check failed: ' + this.result.error;
      const score = this.result.risk_score;
      const sigs = (this.result.signals || []).map(s => SIGNAL_META[s.name]?.label || s.name).join(', ');
      return `Risk: ${this.riskLabel}${score ? ' (' + score + '/100)' : ''}${sigs ? ' · ' + sigs : ''}`;
    },
  },

  watch: {
    address(v) { if (v && this.autoCheck) this.check(); },
    chain()    { if (this.address && this.autoCheck) this.check(); },
  },

  mounted() {
    if (this.address && this.autoCheck) this.check();
  },

  methods: {
    sigMeta(name) { return SIGNAL_META[name] || { icon: '⚠', label: name }; },

    async check() {
      if (!this.address) return;
      this.loading = true;
      try {
        const r = await checkAddress(this.address, this.blockchain);
        this.result = r;
        this.$emit('result', r);
        if (r.hardBlocked || r.softBlocked) this.$emit('blocked', r);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.aml-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: .68rem;
  font-weight: 600;
  letter-spacing: .04em;
  padding: 2px 7px;
  border-radius: 20px;
  border: 1px solid rgba(255,255,255,.1);
  background: rgba(255,255,255,.05);
  cursor: default;
  white-space: nowrap;
  vertical-align: middle;
  user-select: none;
}
.aml-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
.aml-text { color: #ccc; }
.aml-low      .aml-text { color: #28a745; }
.aml-medium   .aml-text { color: #ffc107; }
.aml-high     .aml-text { color: #fd7e14; }
.aml-critical .aml-text { color: #dc3545; }

.aml-tags { display: flex; gap: 3px; flex-wrap: wrap; }
.aml-tag  { font-size: .6rem; padding: 1px 4px; background: rgba(255,255,255,.06); border-radius: 3px; color: #aaa; }
.aml-tag-more { font-size: .6rem; color: #666; }

.aml-hardblock { border-color: rgba(220,53,69,.4); background: rgba(220,53,69,.1); }
.aml-softblock { border-color: rgba(253,126,20,.3); background: rgba(253,126,20,.08); }

.aml-compact { padding: 2px 5px; }
.aml-compact .aml-dot { width: 8px; height: 8px; }

/* Spinner */
.aml-spin {
  display: inline-block;
  width: 8px; height: 8px;
  border: 1.5px solid rgba(255,255,255,.2);
  border-top-color: #aaa;
  border-radius: 50%;
  animation: amlSpin .7s linear infinite;
}
@keyframes amlSpin { to { transform: rotate(360deg); } }
</style>
